import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer } from '@/components/layout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { KeyRound, CheckCircle2, AlertCircle, Clock, BarChart2, Copy, Eye, EyeOff, Shield, Info, Code, HelpCircle, Users } from 'lucide-react';
import { API_BASE_URL } from '@/api/apiConfig';

const API_BASE = `${API_BASE_URL}/api/keys`;
const USER_API_BASE = `${API_BASE_URL}/api/users`;

interface ApiKey {
  id: number;
  userId: string;
  apiKey: string;
  status: 'ACTIVE' | 'REVOKED' | 'EXPIRED';
  createdAt: string;  // 백엔드 API: createdAt (발급일)
  expiredAt: string;  // 백엔드 API: expiredAt (만료일)
  issuedBy: string;   // 백엔드 API: issuedBy
  revokedAt: string | null;
  revokedBy: string | null;
  allowedDomains: string | null;  // 허용된 도메인 목록 (JS 스니펫용)
  valid?: boolean;
}

interface ApiKeyHistory {
  id: number;
  userId: string;
  apiKey: string;
  action: 'ISSUED' | 'REVOKED' | 'EXPIRED';
  performedBy: string;
  performedAt: string;
}

interface UserInfo {
  userId: string;
  name: string;
  email: string;
  userRole: string;
}

export default function ApiKeyManagement() {
  const { user } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const isFreePlan = user?.subscriptionPlan === 'FREE' && !isSuperAdmin;
  
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [history, setHistory] = useState<ApiKeyHistory[]>([]);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [expiredSoon, setExpiredSoon] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ total: number; used: number; remaining: number } | null>(null);
  const [activeKey, setActiveKey] = useState<ApiKey | null>(null);
  const [showApiKey, setShowApiKey] = useState<string | null>(null); // 마스킹 해제용
  const [usageLoading, setUsageLoading] = useState(false);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [allowedDomains, setAllowedDomains] = useState(''); // 도메인 입력 필드
  const [showDomainEdit, setShowDomainEdit] = useState(false); // 도메인 수정 모달
  const [editingDomains, setEditingDomains] = useState(''); // 수정 중인 도메인
  
  // SUPER_ADMIN 전용 상태
  const [searchResults, setSearchResults] = useState<UserInfo[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const searchDropdownRef = React.useRef<HTMLDivElement | null>(null);

  const isKeyValid = (key: ApiKey) => {
    if (key.valid !== undefined) return key.valid;
    const expired = key.expiredAt ? new Date(key.expiredAt).getTime() <= Date.now() : false;
    return key.status === 'ACTIVE' && !expired;
  };

  const isDateExpired = (key: ApiKey) => {
    return key.expiredAt ? new Date(key.expiredAt).getTime() <= Date.now() : false;
  };

  const expiredActiveKey = apiKeys.find((key) => key.status === 'ACTIVE' && !isKeyValid(key));

  // 토스트 표시 함수
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // SUPER_ADMIN: 검색어 기반 사용자 조회 (서버 사이드 검색)
  const searchUsers = async (keyword: string) => {
    if (!isSuperAdmin) return;
    if (keyword.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      // 검색어로 사용자 검색 - 최대 50개만 가져옴
      const res = await axios.get(`${USER_API_BASE}?keyword=${encodeURIComponent(keyword)}&page=0&size=50`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const userList = res.data.data || res.data.content || res.data;
      setSearchResults(Array.isArray(userList) ? userList : []);
      setShowSearchResults(true);
    } catch (e: any) {
      console.error('사용자 검색 실패', e);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // 검색어 입력 시 debounce 적용 (500ms)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (userSearchKeyword.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(userSearchKeyword);
      }, 500);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [userSearchKeyword]);

  // 검색 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  // 현재 조회 대상 userId (SUPER_ADMIN이면 선택한 사용자, 아니면 본인)
  const targetUserId = isSuperAdmin && selectedUser ? selectedUser.userId : user?.userId;

  // 사용량/쿼터 조회 함수
  const fetchUsage = async (apiKey: string) => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      // apiOpenAlt.getUsage는 X-API-KEY 헤더 필요
      const { apiOpenAlt } = await import('@/api/apiOpenAlt');
      const data = await apiOpenAlt.getUsage(apiKey);
      setUsage(data);
    } catch (e: any) {
      setUsageError(e.response?.data?.message || 'API 사용량 조회 실패');
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      // SUPER_ADMIN이 사용자를 선택한 경우 targetUserId 파라미터 추가
      const params = isSuperAdmin && selectedUser ? `?targetUserId=${selectedUser.userId}` : '';
      const res = await axios.get(`${API_BASE}/list${params}`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      
      // 백엔드 응답 구조: { success, data, targetUserId }
      const raw = res.data.data ?? res.data;
      const apiKeyList: ApiKey[] = Array.isArray(raw) ? raw : [];
      setApiKeys(apiKeyList);
      
      // 실제 사용 가능한 키 찾기
      const active = apiKeyList.find((k: ApiKey) => isKeyValid(k));
      setActiveKey(active || null);
    } catch (e: any) {
      setError(e.response?.data?.message || 'API Key 조회 실패');
    }
    setLoading(false);
  };

  const issueKey = async () => {
    // 무료 플랜 체크
    if (isFreePlan) {
      alert('API 연동(API Key 발급)은 스타트 플랜 이상만 가능합니다.\n플랜 업그레이드 후 API Key를 발급받으세요.');
      return;
    }

    // 회원 권한 체크
    if (!user || !user.userId) {
      setError('API Key 발급은 회원만 가능합니다. 로그인 후 다시 시도해주세요.');
      return;
    }

    // SUPER_ADMIN이 사용자를 선택하지 않은 경우
    if (isSuperAdmin && !selectedUser) {
      setError('발급받을 사용자를 선택해주세요.');
      return;
    }

    // 기존 활성 키가 있으면 경고
    if (activeKey) {
      const targetUserName = isSuperAdmin && selectedUser 
        ? `${selectedUser.name}(${selectedUser.userId})`
        : '본인';
      if (!window.confirm(`${targetUserName}의 기존 활성 키가 자동으로 폐기됩니다. 계속하시겠습니까?`)) {
        return;
      }
    }

    setLoading(true);
    setError(null);
    setNewKey(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      // SUPER_ADMIN이 다른 사용자의 키를 발급하는 경우
      const params = new URLSearchParams();
      if (isSuperAdmin && selectedUser) {
        params.append('targetUserId', selectedUser.userId);
      }
      if (allowedDomains.trim()) {
        params.append('allowedDomains', allowedDomains.trim());
      }
      const queryString = params.toString();
      const res = await axios.post(`${API_BASE}/issue${queryString ? '?' + queryString : ''}`, {}, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      
      // 백엔드 응답 구조: { success, message, data: { apiKey, ... } }
      const apiKeyData = res.data.data || res.data;
      const apiKey = apiKeyData.apiKey;
      
      setNewKey(apiKey);
      setShowApiKey(apiKey); // 전체 키 표시
      
      const message = res.data.message || '새 API Key가 발급되었습니다! 안전한 곳에 보관하세요.';
      showToast(message, 'success');
      await fetchKeys();
      setAllowedDomains(''); // 도메인 입력 초기화
    } catch (e: any) {
      setError(e.response?.data?.message || 'API Key 발급 실패');
    }
    setLoading(false);
  };

  const updateDomains = async (apiKey: string) => {
    if (!editingDomains.trim()) {
      setError('도메인을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      const params = new URLSearchParams();
      if (isSuperAdmin && selectedUser) {
        params.append('userId', selectedUser.userId);
      }
      params.append('allowedDomains', editingDomains.trim());
      
      const res = await axios.put(`${API_BASE}/update-domains/${apiKey}?${params.toString()}`, {}, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      
      const message = res.data?.message || '도메인이 수정되었습니다.';
      showToast(message, 'success');
      await fetchKeys();
      setShowDomainEdit(false);
      setEditingDomains('');
    } catch (e: any) {
      setError(e.response?.data?.message || '도메인 수정 실패');
    }
    setLoading(false);
  };

  const revokeKey = async (apiKey: string) => {
    if (!window.confirm('정말로 이 API Key를 폐기하시겠습니까?\n\n폐기 후에는 복구할 수 없으며, 해당 키로는 더 이상 API를 호출할 수 없습니다.')) return;
    
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      // SUPER_ADMIN이 다른 사용자의 키를 폐기하는 경우 userId 파라미터 추가
      const params = isSuperAdmin && selectedUser ? `?userId=${selectedUser.userId}` : '';
      const res = await axios.delete(`${API_BASE}/revoke/${apiKey}${params}`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      
      setNewKey(null);
      setShowApiKey(null);
      
      const message = res.data?.message || 'API Key가 폐기되었습니다.';
      showToast(message, 'success');
      await fetchKeys();
    } catch (e: any) {
      setError(e.response?.data?.message || 'API Key 폐기 실패');
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    setShowHistory(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      // SUPER_ADMIN이 사용자를 선택한 경우 targetUserId 파라미터 추가
      const params = isSuperAdmin && selectedUser ? `?targetUserId=${selectedUser.userId}` : '';
      const res = await axios.get(`${API_BASE}/history${params}`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      
      // 백엔드 응답 구조: { success, data, targetUserId }
      const rawHistory = res.data.data ?? res.data;
      const historyData: ApiKeyHistory[] = Array.isArray(rawHistory) ? rawHistory : [];
      setHistory(historyData);
    } catch (e: any) {
      setError(e.response?.data?.message || '이력 조회 실패');
    }
  };

  useEffect(() => {
    if (user?.userId || (isSuperAdmin && selectedUser)) {
      fetchKeys();
    }
  }, [user?.userId, selectedUser]);

  return (
    <PageContainer maxWidth="full">
      {/* 토스트 알림 */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
          <Alert className={toast.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}>
            {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-600" />}
            <AlertDescription className={toast.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {toast.message}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><KeyRound className="w-7 h-7 text-primary" />API Key 관리</h1>
          <p className="text-gray-600 mt-2">API Key 발급, 만료, 폐기 및 이력 관리</p>
        </div>
        <Button variant="outline" onClick={() => setShowGuide(!showGuide)}>
          <HelpCircle className="w-4 h-4 mr-2" />
          사용 가이드
        </Button>
      </div>

      {/* 사용 가이드 */}
      {showGuide && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Info className="w-5 h-5" />
              API Key 사용 가이드
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">📌 주요 정책</h4>
              <ul className="list-disc pl-5 space-y-1 text-gray-700">
                <li>사용자당 <strong>1개의 활성 API Key만 유지</strong>됩니다</li>
                <li>새 키 발급 시 기존 활성 키는 <strong>자동으로 폐기</strong>됩니다</li>
                <li>API Key는 <strong>만료되지 않습니다 (무제한)</strong></li>
                <li>API Key는 <strong>최초 1회만 전체 노출</strong>되며, 이후에는 마스킹 처리됩니다</li>
                <li className="text-orange-700 font-semibold">API Key 발급은 <strong>스타트 플랜 이상</strong> 회원만 가능합니다 (무료 플랜 제한)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                보안 권장사항
              </h4>
              <ul className="list-disc pl-5 space-y-1 text-gray-700">
                <li>발급받은 API Key는 <strong>안전한 곳에 보관</strong>하세요 (환경변수, 암호화된 저장소 등)</li>
                <li>API Key를 <strong>절대 코드에 직접 포함하거나 공개 저장소에 커밋하지 마세요</strong></li>
                <li>API Key가 노출되었다면 즉시 폐기하고 새로 발급받으세요</li>
                <li>사용하지 않는 키는 즉시 폐기하세요</li>
                <li>정기적으로 키를 재발급하여 보안을 강화하세요</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Code className="w-4 h-4" />
                사용 예시
              </h4>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-xs">{`// JavaScript/Node.js 예시
fetch('https://alt.aigeneration-tech.com/api/open/alt/generate', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': '발급받은-API-KEY'  // UUID 형식 (alt_ 접두사 없음)
  },
  body: JSON.stringify({
    imageUrl: 'https://example.com/image.jpg'
    // useContextProfile: true  // 선택사항, 기본값 true
  })
})
  .then(res => res.json())
  .then(data => console.log(data));`}</pre>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-yellow-900 text-xs">
                <strong>⚠️ 주의:</strong> API Key는 복구할 수 없습니다. 발급 시 반드시 안전한 곳에 복사해 두세요.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 무료 플랜 안내 배너 */}
      {isFreePlan && (
        <Alert className="border-orange-400 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>무료 플랜은 API 연동을 지원하지 않습니다.</strong>{' '}
            API Key 발급은 스타트 플랜 이상 회원에게만 제공됩니다.{' '}
            <a href="/dashboard/my/plan" className="underline font-semibold hover:text-orange-900">플랜 업그레이드 바로가기</a>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {newKey && (
        <Alert className="border-green-500 bg-green-50 text-green-900">
          <CheckCircle2 className="w-4 h-4 mr-2 inline" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold">✅ 새 API Key가 발급되었습니다!</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-white px-3 py-2 rounded border border-green-300 flex-1 font-mono text-sm">
                {newKey}
              </code>
              <Button size="sm" onClick={() => {
                navigator.clipboard.writeText(newKey);
                showToast('클립보드에 복사되었습니다!', 'success');
              }}>
                <Copy className="w-3 h-3 mr-1" />
                복사
              </Button>
            </div>
            <div className="text-xs text-red-600 font-semibold">
              ⚠️ 보안을 위해 지금 바로 안전한 곳에 복사해 두세요. 이 화면을 벗어나면 전체 키를 다시 볼 수 없습니다.
            </div>
          </div>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {menuIcon && <span className="text-2xl">{menuIcon}</span>}
                  {menuName || 'API Key 관리'}
                </CardTitle>
                <CardDescription>
                  {isSuperAdmin 
                    ? '사용자를 선택하여 API Key를 관리할 수 있습니다' 
                    : '본인의 API Key 목록'
                  }
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {!user || !user.userId ? (
                  <Alert className="bg-red-50 border-red-200 text-red-800 py-2 px-3">
                    <AlertCircle className="w-4 h-4 mr-2 inline" />
                    로그인이 필요합니다.
                  </Alert>
                ) : (
                  <>
                    <div className="flex-1 max-w-md">
                      <Input
                        placeholder="허용 도메인 (선택사항, JS 스니펫용)"
                        value={allowedDomains}
                        onChange={(e) => setAllowedDomains(e.target.value)}
                        disabled={loading || isFreePlan || (isSuperAdmin && !selectedUser)}
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        예: example.com 또는 example.com,*.subdomain.com (콤마로 구분)
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        ⚠️ JS 스니펫 사용 시 반드시 설정 필요: API 키 유출 시 다른 사이트에서 사용 방지를 위해 도메인 제한을 적용합니다. 백엔드 직접 연동이나 크롬 확장에서는 필요하지 않습니다.
                      </p>
                    </div>
                    <Button 
                      onClick={issueKey} 
                      disabled={loading || isFreePlan || (isSuperAdmin && !selectedUser)} 
                      variant={isFreePlan ? 'outline' : 'default'}
                      title={isFreePlan ? 'API Key 발급은 스타트 플랜 이상만 가능합니다' : undefined}
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      {activeKey ? '새 API Key 발급 (기존 키 폐기)' : 'API Key 발급'}
                      {isFreePlan && <span className="ml-1 text-xs text-orange-500">(유료 플랜 전용)</span>}
                    </Button>
                    <Button 
                      onClick={fetchHistory} 
                      variant="secondary" 
                      disabled={loading || isFreePlan || (isSuperAdmin && !selectedUser)}
                    >
                      이력 조회
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* SUPER_ADMIN 전용: 사용자 검색 및 선택 */}
            {isSuperAdmin && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="font-semibold">대상 사용자 선택</span>
                </div>
                
                {selectedUser ? (
                  // 선택된 사용자 표시
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">선택된 사용자</div>
                        <div className="font-semibold text-lg">{selectedUser.name}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          <div>ID: {selectedUser.userId}</div>
                          <div>Email: {selectedUser.email}</div>
                          {selectedUser.userRole && <div>Role: {selectedUser.userRole}</div>}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearchKeyword('');
                          setSearchResults([]);
                          setShowSearchResults(false);
                        }}
                      >
                        변경
                      </Button>
                    </div>
                  </div>
                ) : (
                  // 사용자 검색 UI
                  <div className="relative" ref={searchDropdownRef}>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        사용자 검색 (최소 2글자 입력)
                      </label>
                      <Input
                        placeholder="사용자 ID, 이름, 이메일로 검색..."
                        value={userSearchKeyword}
                        onChange={(e) => setUserSearchKeyword(e.target.value)}
                        className="w-full"
                      />
                      {searching && (
                        <div className="absolute right-3 top-9 text-sm text-gray-500">
                          검색 중...
                        </div>
                      )}
                    </div>
                    
                    {/* 검색 결과 드롭다운 */}
                    {showSearchResults && searchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        {searchResults.map((u) => (
                          <div
                            key={u.userId}
                            className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                            onClick={() => {
                              setSelectedUser(u);
                              setShowSearchResults(false);
                              setUserSearchKeyword('');
                            }}
                          >
                            <div className="font-semibold">{u.name}</div>
                            <div className="text-sm text-gray-600">
                              {u.userId} - {u.email}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* 검색 결과 없음 */}
                    {showSearchResults && searchResults.length === 0 && !searching && userSearchKeyword.length >= 2 && (
                      <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 text-center">
                        "{userSearchKeyword}"에 대한 검색 결과가 없습니다.
                      </div>
                    )}
                    
                    {/* 안내 메시지 */}
                    {userSearchKeyword.length > 0 && userSearchKeyword.length < 2 && (
                      <div className="mt-2 text-xs text-gray-500">
                        최소 2글자 이상 입력해주세요.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {activeKey && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-green-900">현재 활성 API Key</h3>
                <Badge className="bg-green-600 text-white">활성</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <code className="bg-white px-3 py-2 rounded border border-green-300 flex-1 min-w-0 font-mono text-sm break-all">
                  {showApiKey === activeKey.apiKey 
                    ? activeKey.apiKey 
                    : activeKey.apiKey
                      ? `${activeKey.apiKey.substring(0, 8)}****${activeKey.apiKey.substring(activeKey.apiKey.length - 4)}`
                      : '(알 수 없음)'
                  }
                </code>
                <Button size="sm" variant="outline" onClick={() => {
                  setShowApiKey(showApiKey === activeKey.apiKey ? null : activeKey.apiKey);
                }}>
                  {showApiKey === activeKey.apiKey ? (
                    <><EyeOff className="w-3 h-3 mr-1" />마스킹</>
                  ) : (
                    <><Eye className="w-3 h-3 mr-1" />보기</>
                  )}
                </Button>
                <Button size="sm" onClick={() => {
                  navigator.clipboard.writeText(activeKey.apiKey);
                  showToast('클립보드에 복사되었습니다!', 'success');
                }}>
                  <Copy className="w-3 h-3 mr-1" />
                  복사
                </Button>
                <Button size="sm" variant="destructive" onClick={() => revokeKey(activeKey.apiKey)} disabled={loading}>
                  폐기
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  setShowDomainEdit(true);
                  setEditingDomains(activeKey.allowedDomains || '');
                }} disabled={loading}>
                  도메인 수정
                </Button>
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>발급일: {new Date(activeKey.createdAt).toLocaleString('ko-KR')}</div>
                <div>만료일: {new Date(activeKey.expiredAt).toLocaleString('ko-KR')}</div>
                {activeKey.allowedDomains && (
                  <div>허용 도메인: {activeKey.allowedDomains}</div>
                )}
              </div>
            </div>
          )}

          {!activeKey && !loading && (
            <div className="text-center py-8 text-gray-500">
              <KeyRound className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>활성화된 API Key가 없습니다.</p>
              <p className="text-sm">위의 'API Key 발급' 버튼을 클릭하여 발급받으세요.</p>
            </div>
          )}

          <div className="mt-6">
            <h3 className="font-semibold mb-3">전체 이력 ({apiKeys.length}개)</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>API Key</TableHead>
                    <TableHead>상태</TableHead>
                    <TableHead>발급일</TableHead>
                    <TableHead>폐기일</TableHead>
                    <TableHead>허용 도메인</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-400 py-8">API Key가 없습니다</TableCell>
                    </TableRow>
                  ) : (
                    apiKeys.map((k) => (
                      <TableRow key={k.id}>
                        <TableCell className="font-mono text-xs">
                          {k.apiKey ? `${k.apiKey.substring(0, 8)}****${k.apiKey.substring(k.apiKey.length - 4)}` : '(알 수 없음)'}
                        </TableCell>
                        <TableCell>
                          {isKeyValid(k) && <Badge className="bg-green-100 text-green-800">활성</Badge>}
                          {k.status === 'ACTIVE' && !isKeyValid(k) && <Badge className="bg-red-100 text-red-800">만료</Badge>}
                          {k.status === 'REVOKED' && <Badge className="bg-gray-200 text-gray-700">폐기</Badge>}
                          {k.status === 'EXPIRED' && <Badge className="bg-red-100 text-red-800">만료</Badge>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {new Date(k.createdAt).toLocaleDateString('ko-KR')}
                        </TableCell>
                        <TableCell className="text-xs">
                          {k.revokedAt ? new Date(k.revokedAt).toLocaleDateString('ko-KR') : '-'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {k.allowedDomains || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      {showHistory && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle>API Key 변경 이력</CardTitle>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const key = activeKey?.apiKey;
                    if (key) fetchUsage(key);
                    else alert('활성화된 API Key가 없습니다.');
                  }}
                  disabled={usageLoading || !activeKey}
                >
                  <BarChart2 className="w-4 h-4 mr-1" />
                  사용량/쿼터 조회
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* 사용량/쿼터 정보 */}
            {usageLoading && <div className="text-sm text-gray-500 mb-2">조회 중...</div>}
            {usageError && <div className="text-sm text-red-600 mb-2">{usageError}</div>}
            {usage && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">API 사용량</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">총 쿼터</div>
                    <div className="text-lg font-bold text-blue-900">{usage.total}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">사용량</div>
                    <div className="text-lg font-bold text-blue-900">{usage.used}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">남은 쿼터</div>
                    <div className="text-lg font-bold text-green-600">{usage.remaining}</div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>행위</TableHead>
                    <TableHead>API Key</TableHead>
                    <TableHead>행위자</TableHead>
                    <TableHead>일시</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-gray-400 py-8">
                        이력 데이터가 없습니다
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>
                          {h.action === 'ISSUED' && <Badge className="bg-blue-100 text-blue-800">발급</Badge>}
                          {h.action === 'REVOKED' && <Badge className="bg-gray-200 text-gray-700">폐기</Badge>}
                          {h.action === 'EXPIRED' && <Badge className="bg-red-100 text-red-800">만료</Badge>}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {h.apiKey ? `${h.apiKey.substring(0, 8)}****${h.apiKey.substring(h.apiKey.length - 4)}` : '(알 수 없음)'}
                        </TableCell>
                        <TableCell>{h.performedBy}</TableCell>
                        <TableCell className="text-xs">
                          {new Date(h.performedAt).toLocaleString('ko-KR')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 도메인 수정 모달 */}
      <Dialog open={showDomainEdit} onOpenChange={setShowDomainEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>허용 도메인 수정</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">허용 도메인</label>
              <Input
                placeholder="example.com 또는 example.com,*.subdomain.com (콤마로 구분)"
                value={editingDomains}
                onChange={(e) => setEditingDomains(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-2">
                예: example.com 또는 example.com,*.subdomain.com (콤마로 구분)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                ⚠️ JS 스니펫 사용 시 반드시 설정 필요: API 키 유출 시 다른 사이트에서 사용 방지를 위해 도메인 제한을 적용합니다.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDomainEdit(false)}>
              취소
            </Button>
            <Button onClick={() => activeKey && updateDomains(activeKey.apiKey)} disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
